import React, {useState} from 'react';
import {useT} from 'react-polyglot-hooks';
import {Redirect, Switch} from 'react-router-dom';
import useRouter from 'use-react-router';
import {CanRoute} from './components/Can';
import {RouteWithLayout} from './components/RouteWithLayout';
import addLog from './graphql/addLog.api';
import {getMe} from './graphql/auth';
import {WcvLogCodes, WcvLogLevel, WcvType} from './graphql/logs.datatypes';
import {Main as MainLayout, Minimal as MinimalLayout} from './layouts';
import {initValidators} from './utils/validators';


import BillingPartyDetails, {BillingPartyInMyGroupDetails} from './views/BillingParty/BillingPartyDetails/BillingPartyDetails';
import BillingPartyList from './views/BillingParty/BillingPartyList';
import CommsDetails from './views/Comms/CommsDetails';
import CommsList from './views/Comms/CommsList';
import CreditCardFee from './views/CreditCardFee/CreditCardFee';
import GroupBookingDetails from './views/GroupBookings/GroupBookingDetails';
import GroupBookingList from './views/GroupBookings/GroupBookingList';
import GroupDetails from './views/Groups/GroupDetails';
import GroupList from './views/Groups/GroupList';
import GroupMemberDetails from './views/Groups/GroupMemberDetails';
import GroupMemberList from './views/Groups/GroupMemberList';
import ForgotPassword from './views/LogIn/ForgotPassword';
import ResetPassword from './views/LogIn/ResetPassword';
import LogList from './views/Logs/LogList';
import ReportDetails from './views/Reports/ReportDetails';
import ReportsList from './views/Reports/ReportsList';
import ResourcesList from './views/Resources/ResourceList';
import RoleDetails from './views/Roles/RoleDetails';
import RoleList from './views/Roles/RoleList';
import UserDetails from './views/Users/UserDetails';
import UserList from './views/Users/UserList';
import VerifyEmail from './views/Users/VerifyEmail';
import BookingTool from './views/BookingTool';
import Loading from './views/Loading';
import Maintenance from './views/Maintenance';
import LogIn from './views/LogIn';
import Dashboard from './views/Dashboard';
import NotAuthorised from './views/NotAuthorised';
import NotFound from './views/NotFound';
import RoomPartyList from './views/RoomParty/RoomPartyList';
import RoomPartyDetails from './views/RoomParty/RoomPartyDetails';
import CmsCopyList from './views/CmsCopy/CmsCopyList';
import CmsCopyDetails from './views/CmsCopy/CmsCopyDetails/CmsCopyDetails';
import AdminBookingTool from './views/BookingTool/AdminBookingTool';
import MyBillingPartyList from './views/BillingParty/MyBillingPartyList';
import PaymentsList from './views/Payments/PaymentsList';
import DocumentsList from './views/Documents/DocumentsList';
import DocumentDetails from './views/Documents/DocumentDetails';
import NetsuiteCsvDownload from './views/Netsuite/NetsuiteCsvDownload';
import ReceiptsPerGroupDownload from './views/ReceiptsPerGroup';
import {MyGroupList} from './views/Groups/GroupList/GroupList';
import {MyGroupDetails} from './views/Groups/GroupDetails/GroupDetails';
import {MyGroupMemberDetails} from './views/Groups/GroupMemberDetails/GroupMemberDetails';
import {RoomPartyInMyGroupDetails} from './views/RoomParty/RoomPartyDetails/RoomPartyDetails';
import Contact from './views/Contact';
import MyRoomPartyList from './views/RoomParty/MyRoomPartyList';
import MyTravellersList from './views/Groups/MyTravellersList';
import MyDocuments from './views/Documents/MyDocuments';
import MyGroupMembers from './views/Groups/MyGroupMembers';
import {MyGroupMembersPendingApprovalList} from './views/Groups/GroupMembersPendingApprovalList/GroupMembersPendingApprovalList';
import {MyGroupLogList} from './views/Logs/LogList/LogList';
import {OverdueDocumentList} from './views/Documents/OverdueDocumentsList';
import EmailList from './views/EmailManagement/EmailList';
import TripDetails from './views/Dashboard/TripDetails';

let listenerAdded = false;

const Routes = () => {
    const {history} = useRouter();

    // tslint:disable-next-line:no-unused-expression
    // @ts-ignore
    const maintenanceTag = document.head.querySelector('[name~="display-maintenance-page"]');
    // tslint:disable-next-line:no-unused-expression
    // @ts-ignore
    const content = maintenanceTag ? maintenanceTag.content : false;

    if(process.env.NODE_ENV === 'production' && content == true) {
        return (
            <Switch>
                <RouteWithLayout
                    exact path={history.location.pathname}
                    component={Maintenance} layout={MinimalLayout}
                />
            </Switch>
        );
    }

    const userData = getMe();
    const loading = userData.loading;
    const loggedIn = !!(userData.data && userData.data.me);
    const [sendAddLog] = addLog(false);
    const [pathname, setPathname] = useState('');

    //Init validators in here so that translations are available.
    initValidators(useT());

    if (loading) return (
        <Switch>
            <RouteWithLayout
                exact path={history.location.pathname}
                component={Loading} layout={MinimalLayout}
            />
        </Switch>
    );

    if (!listenerAdded) {
        listenerAdded = true;
        //Only add listnener the first time this component is loaded.
        history.listen((location: any) => {
            if (pathname !== location.pathname) {

                const sanitisedData = {
                    data: {
            level: WcvLogLevel.INFO,
            user_id: loggedIn ? userData!.data!.me!.id : null,
            type: WcvType.SYSTEM,
            type_id: null,
            code: WcvLogCodes.PAGE_VIEW,
            description: `Page viewed ${location.pathname}`,
            payload: `{"path": "${location.pathname}"}`,
          }
        };
        sendAddLog({variables: sanitisedData});
        setPathname(location.pathname);
      }
    });
  }

  return (
    <Switch>
      <Redirect exact from="/" to={loggedIn ? '/dashboard' : '/login'} />

      {/*  Routes available to non logged in users */}
        <RouteWithLayout
            exact path="/login"
            component={LogIn} layout={MinimalLayout}
        />
        <RouteWithLayout
            exact path="/password/forgot"
            component={ForgotPassword} layout={MinimalLayout}
        />
        <RouteWithLayout
            exact path="/password/reset/:token/:email/:expire"
            component={ResetPassword} layout={MinimalLayout}
        />
        <RouteWithLayout
            exact path="/email/verify/:id/:email"
            component={VerifyEmail} layout={MinimalLayout}
        />
        <RouteWithLayout
            exact path="/booking/:id"
            component={BookingTool} layout={MinimalLayout}
        />


        {/*  Redirect users that are not logged in */}
        {!loggedIn && history.location.pathname !== '/login' && (
            <Redirect to="/login"/>
        )
        }

        {/* Routes available to logged in users */}
        <RouteWithLayout
            exact path="/dashboard"
            component={Dashboard} layout={MainLayout}
        />
        <RouteWithLayout
            exact path="/contact"
            component={Contact} layout={MainLayout}
        />
        <CanRoute
            exact path="/my_events/:id"
            component={TripDetails} layout={MainLayout}
            permission="associated.groups.bookings.read" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/users"
            component={UserList} layout={MainLayout}
            permission="user.read" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/users/:id"
            component={UserDetails} layout={MainLayout}
            permission="user.read" fail={NotAuthorised}
        />

        <CanRoute
            exact path="/roles"
            component={RoleList} layout={MainLayout}
            permission="role.read" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/roles/:id"
            component={RoleDetails} layout={MainLayout}
            permission="role.update" fail={NotAuthorised}
        />

        <CanRoute
            exact path="/logs"
            component={LogList} layout={MainLayout}
            permission="log.read" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/logs/:code"
            component={LogList} layout={MainLayout}
            permission="log.read" fail={NotAuthorised}
        />

        <CanRoute
            exact path="/comms"
            component={CommsList} layout={MainLayout}
            permission="communication.read" fail={NotAuthorised}
        />

        <CanRoute 
            exact path="/credit_card_fee"
            component={CreditCardFee} layout={MainLayout}
            permission="user.update" fail={NotAuthorised}
        />
        
        <CanRoute
            exact path="/comms/:id"
            component={CommsDetails} layout={MainLayout}
            permission="communication.update" fail={NotAuthorised}
        />

        <CanRoute
            exact path="/reports"
            component={ReportsList} layout={MainLayout}
            permission="reports.create" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/reports/:key"
            component={ReportDetails} layout={MainLayout}
            permission="reports.create" fail={NotAuthorised}
        />

        <CanRoute
            exact path="/resources"
            component={ResourcesList} layout={MainLayout}
            permission="resource.create" fail={NotAuthorised}
        />

        <CanRoute
            exact path="/groups"
            component={GroupList} layout={MainLayout}
            permission={'groups.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/groups/:id"
            component={GroupDetails} layout={MainLayout}
            permission={'groups.read'} fail={NotAuthorised}
        />

        <CanRoute
            exact path="/mygroups"
            component={MyGroupList} layout={MainLayout}
            permission={'associated.groups.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/mygroups/:ref"
            component={MyGroupDetails} layout={MainLayout}
            permission={'associated.groups.read'} fail={NotAuthorised}
        />

        <CanRoute
            exact path="/groupmembers"
            component={GroupMemberList} layout={MainLayout}
            permission={'groups.read'} fail={NotAuthorised}
        />

        <CanRoute
            exact path="/groupmembers/:id/:group_id?"
            component={GroupMemberDetails} layout={MainLayout}
            permission="groups.update" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/my_group_members"
            component={MyGroupMembers} layout={MainLayout}
            permission={'associated.groups.bookings.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/mygroups/groupmembers/:json"
            component={MyGroupMemberDetails} layout={MainLayout}
            permission={'associated.groups.bookings.read'} fail={NotAuthorised}
        />

        {/*Bookings*/}
        <CanRoute
            exact path="/group_bookings"
            component={GroupBookingList} layout={MainLayout}
            permission={'groups.bookings.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/group_bookings/new/:group_id"
            component={GroupBookingDetails} layout={MainLayout}
            permission={'groups.bookings.create'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/group_bookings/:id"
            component={GroupBookingDetails} layout={MainLayout}
            permission={'groups.bookings.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/admin_booking/:id"
            component={AdminBookingTool} layout={MainLayout}
            permission={'groups.bookings.create'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/admin_booking/:id/:booking_id"
            component={BookingTool} layout={MainLayout}
            permission={'groups.bookings.create'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/booking/:id/:booking_id"
            component={BookingTool} layout={MainLayout}
            permission={'associated.groups.bookings.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/billing_party"
            component={BillingPartyList} layout={MainLayout}
            permission={'groups.bookings.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/billing_party/new/:booking_id"
            component={BillingPartyDetails} layout={MainLayout}
            permission="groups.bookings.create" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/billing_party/:id"
            component={BillingPartyDetails} layout={MainLayout}
            permission="groups.bookings.read" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/my_billing_parties"
            component={MyBillingPartyList} layout={MainLayout}
            permission="associated.groups.bookings.read" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/my_room_parties"
            component={MyRoomPartyList} layout={MainLayout}
            permission="associated.groups.bookings.read" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/my_travelers"
            component={MyTravellersList} layout={MainLayout}
            permission="associated.groups.bookings.read" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/my_pending_approval"
            component={MyGroupMembersPendingApprovalList} layout={MainLayout}
            permission="associated.groups.bookings.read" fail={NotAuthorised}
        />


        <CanRoute
            exact path="/mygroups/billing_party/:json"
            component={BillingPartyInMyGroupDetails} layout={MainLayout}
            permission={'associated.groups.bookings.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/room_party"
            component={RoomPartyList} layout={MainLayout}
            permission={'groups.bookings.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/room_party/new/:booking_id"
            component={RoomPartyDetails} layout={MainLayout}
            permission="groups.bookings.create" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/room_party/:id"
            component={RoomPartyDetails} layout={MainLayout}
            permission="groups.bookings.read" fail={NotAuthorised}
        />
        <CanRoute
            exact path="/mygroups/room_party/:json"
            component={RoomPartyInMyGroupDetails} layout={MainLayout}
            permission={'associated.groups.bookings.read'} fail={NotAuthorised}
        />

        <CanRoute
            exact path="/my_group_logs"
            component={MyGroupLogList} layout={MainLayout}
            permission={'associated.groups.read'} fail={NotAuthorised}
        />


        <CanRoute
            exact path="/cms_copy"
            component={CmsCopyList} layout={MainLayout}
            permission={'cms_copy.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/cms_copy/:id"
            component={CmsCopyDetails} layout={MainLayout}
            permission={['cms_copy.update']} fail={NotAuthorised}
        />

        <CanRoute
            exact path="/payments"
            component={PaymentsList} layout={MainLayout}
            permission={'payments.read'} fail={NotAuthorised}
        />

        <CanRoute
            exact path="/documents"
            component={DocumentsList} layout={MainLayout}
            permission={'documents.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/documents/:id"
            component={DocumentDetails} layout={MainLayout}
            permission={'documents.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/my_documents"
            component={MyDocuments} layout={MainLayout}
            permission={'documents.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/my_groups_overdue_documents"
            component={OverdueDocumentList} layout={MainLayout}
            permission={'documents.read'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/netsuite"
            component={NetsuiteCsvDownload} layout={MainLayout}
            permission={'reports.create'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/netsuite_groups"
            component={NetsuiteCsvDownload} layout={MainLayout}
            permission={'reports.create'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/receipts_per_group"
            component={ReceiptsPerGroupDownload} layout={MainLayout}
            permission={'reports.create'} fail={NotAuthorised}
        />
        <CanRoute
            exact path="/email_management"
            component={EmailList} layout={MainLayout}
            permission={'email.read'} fail={NotAuthorised}
        />
        {/* 404 page with catchall redirect. */}
        <RouteWithLayout
            exact path="/not-found"
            component={NotFound} layout={MinimalLayout}
        />
        <Redirect to="/not-found"/>
    </Switch>
  );
};

export default Routes;
